import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, MapPin, Lock, CheckCircle2, LogIn, UserPlus, Mail, Sparkles } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const KIND_LABEL = { match: "Match", tournament: "Tournament", league: "League", lesson: "Lesson" };
const ROUTE_FOR_KIND = { match: "/matches", tournament: "/tournaments", league: "/leagues", lesson: "/lessons" };

export default function JoinByToken() {
  const { kind, token } = useParams();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref");
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Guest quick-register form (shown when not logged in)
  const [guestMode, setGuestMode] = useState(false);
  const [guest, setGuest] = useState({ email: "", first_name: "", last_name: "" });

  useEffect(() => {
    let cancel = false;
    const url = ref ? `/events/join/${kind}/${token}?ref=${encodeURIComponent(ref)}` : `/events/join/${kind}/${token}`;
    api.get(url)
      .then((r) => { if (!cancel) setInfo(r.data); })
      .catch((e) => {
        if (cancel) return;
        setError(formatApiError(e.response?.data?.detail) || "Invalid or expired link.");
      })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [kind, token, ref]);

  const refQS = ref ? `?ref=${encodeURIComponent(ref)}` : "";

  const join = async () => {
    if (!user) { goLogin(); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/events/join/${kind}/${token}${refQS}`);
      toast.success(data.already ? "You're already in." : "Joined!");
      navigate(`${ROUTE_FOR_KIND[kind] || `/${kind}s`}/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const goLogin = () => {
    sessionStorage.setItem("post_login_redirect", `/join/${kind}/${token}${refQS}`);
    navigate("/login");
  };

  const guestJoin = async (e) => {
    e.preventDefault();
    if (!guest.email || !guest.first_name) { toast.error("Email and first name are required"); return; }
    setBusy(true);
    try {
      await api.post("/auth/quick-register", guest);
      if (refreshMe) await refreshMe();
      const { data } = await api.post(`/events/join/${kind}/${token}${refQS}`);
      toast.success("Account created — you're in! Check your email for sign-in details.");
      navigate(`${ROUTE_FOR_KIND[kind] || `/${kind}s`}/${data.id}`);
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      if (err.response?.status === 409) {
        toast.error("Email already registered — please sign in instead");
        sessionStorage.setItem("post_login_redirect", `/join/${kind}/${token}${refQS}`);
        navigate("/login");
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto pp-card-elevated p-10 mt-12 animate-pulse">
          <div className="h-6 w-48 bg-white/10 rounded mb-4" />
          <div className="h-4 w-full bg-white/5 rounded mb-2" />
          <div className="h-4 w-2/3 bg-white/5 rounded" />
        </div>
      </AppLayout>
    );
  }

  if (error || !info) {
    return (
      <AppLayout>
        <div className="max-w-xl mx-auto pp-card-elevated p-10 mt-12 text-center" data-testid="join-error">
          <div className="text-[#FF3B30] font-bold mb-2">Couldn't join</div>
          <p className="text-zinc-400 text-sm">{error || "Invalid or expired link."}</p>
          <Link to="/" className="pp-btn-primary inline-block mt-6 px-5 py-2 rounded-lg">Back to home</Link>
        </div>
      </AppLayout>
    );
  }

  const e = info.event;
  const sportName = info.sport?.name;
  const isPrivate = e.visibility === "private";
  const allDay = !!e.all_day;
  const when = e.start
    ? (allDay ? new Date(e.start).toLocaleDateString() + " · All day" : new Date(e.start).toLocaleString())
    : null;
  const price = e.price ? `${e.price} ${e.currency}` : "Free";

  return (
    <AppLayout>
      <div className="max-w-xl mx-auto mt-10" data-testid="join-page">
        <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-2 flex-wrap">
          <span className="pp-chip">{KIND_LABEL[kind] || kind}</span>
          {isPrivate && (
            <span className="pp-chip flex items-center gap-1 text-[#FFD60A] border-[#FFD60A]/40"><Lock size={10} /> Private</span>
          )}
          {sportName && <span className="pp-chip">{sportName}</span>}
          {info.referrer && (
            <span className="pp-chip flex items-center gap-1 text-[#00E676] border-[#00E676]/40" data-testid="join-referrer">
              <Sparkles size={10} /> Invited by {info.referrer.name}
            </span>
          )}
        </div>

        <h1 className="font-heading text-3xl sm:text-4xl font-black">{e.title}</h1>
        {e.description && <p className="text-zinc-400 mt-3">{e.description}</p>}

        <div className="mt-6 pp-card-elevated p-6 space-y-3">
          {when && (
            <div className="flex items-center gap-3 text-sm">
              <Calendar size={16} className="text-[#00E676]" />
              <span>{when}</span>
            </div>
          )}
          {e.location && (
            <div className="flex items-center gap-3 text-sm">
              <MapPin size={16} className="text-[#007AFF]" />
              <span>{e.location}</span>
            </div>
          )}
          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <div className="text-sm text-zinc-400">Price</div>
            <div className="font-bold text-[#00E676]">{price}</div>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {info.already_joined ? (
            <button onClick={() => navigate(`${ROUTE_FOR_KIND[kind] || `/${kind}s`}/${e.id}`)} data-testid="join-go"
              className="pp-btn-secondary w-full px-5 py-3 rounded-xl flex items-center justify-center gap-2">
              <CheckCircle2 size={16} className="text-[#00E676]" /> You're already in — view event
            </button>
          ) : user ? (
            <button onClick={join} data-testid="join-confirm" disabled={busy}
              className="pp-btn-primary w-full px-5 py-3 rounded-xl">{busy ? "Joining…" : `Join this ${kind}`}</button>
          ) : guestMode ? (
            <form onSubmit={guestJoin} className="pp-card-elevated p-5 space-y-3" data-testid="join-guest-form">
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Continue as guest — we'll create your account in 1 click</div>
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="join-guest-first" required placeholder="First name" className="pp-input"
                  value={guest.first_name} onChange={(ev) => setGuest((s) => ({ ...s, first_name: ev.target.value }))} />
                <input data-testid="join-guest-last" placeholder="Last name (optional)" className="pp-input"
                  value={guest.last_name} onChange={(ev) => setGuest((s) => ({ ...s, last_name: ev.target.value }))} />
              </div>
              <input data-testid="join-guest-email" required type="email" placeholder="you@example.com" className="pp-input"
                value={guest.email} onChange={(ev) => setGuest((s) => ({ ...s, email: ev.target.value }))} />
              <p className="text-xs text-zinc-500"><Mail size={11} className="inline" /> Your sign-in password will be emailed to you so you can return later.</p>
              <div className="flex gap-2">
                <button type="button" onClick={() => setGuestMode(false)} className="pp-btn-secondary px-4 py-2 rounded-lg text-sm">Back</button>
                <button data-testid="join-guest-submit" disabled={busy} className="pp-btn-primary flex-1 px-4 py-2 rounded-lg text-sm">{busy ? "Creating…" : "Create account & join"}</button>
              </div>
            </form>
          ) : (
            <>
              <button onClick={goLogin} data-testid="join-login-cta" disabled={busy}
                className="pp-btn-primary w-full px-5 py-3 rounded-xl flex items-center justify-center gap-2">
                <LogIn size={16} /> Sign in & join
              </button>
              <button onClick={() => setGuestMode(true)} data-testid="join-guest-cta"
                className="pp-btn-secondary w-full px-5 py-3 rounded-xl flex items-center justify-center gap-2">
                <UserPlus size={16} /> Continue as guest
              </button>
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
